/**
 * The portal's three roles, from the requirements document section 6.
 *
 * These are real on the backend too, but split across two fields there: an
 * account has a `kind` (Activist Technologies or a customer's person), and a
 * customer's person has a `role` in each organization they belong to. This
 * application keeps one value because one screen is shown at a time - it is
 * the role held in the organization currently open.
 *
 * The two spellings meet only in the helpers at the foot of this file.
 *
 * A plain object rather than a TypeScript `enum`, because enums are one of the
 * constructs newer TypeScript settings reject and they do not erase cleanly.
 */
export const ROLES = {
  /** Internal Activist Technologies staff. Creates and manages organizations. */
  ACTIVIST_STAFF: 'activist_staff',
  /** Organization owner. Everything an Admin can do, plus users and billing. */
  SUPER_ADMIN: 'super_admin',
  /** Organization content editor. */
  ADMIN: 'admin',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

export const ALL_ROLES: readonly Role[] = Object.values(ROLES);

/**
 * How much authority each role carries inside an organization.
 *
 * The requirements say "Super Admins inherit every Admin capability", so a
 * Super Admin passes any check that asks for Admin. Activist Staff are internal
 * and sit on a separate track: they are not an organization role at all, so they
 * do not inherit Admin, and screens for them are gated explicitly.
 */
const ORG_ROLE_RANK: Record<Role, number> = {
  [ROLES.ADMIN]: 1,
  [ROLES.SUPER_ADMIN]: 2,
  [ROLES.ACTIVIST_STAFF]: 0,
};

/** Human-readable labels, for menus and messages. */
export const ROLE_LABELS: Record<Role, string> = {
  [ROLES.ACTIVIST_STAFF]: 'Activist Staff',
  [ROLES.SUPER_ADMIN]: 'Super Admin',
  [ROLES.ADMIN]: 'Admin',
};

/**
 * True when `role` satisfies a requirement for `required`.
 *
 * Super Admin satisfies a requirement for Admin. Activist Staff satisfies only
 * a requirement for Activist Staff.
 */
export function hasRole(role: Role | null, required: Role): boolean {
  if (!role) return false;
  if (role === required) return true;
  if (required === ROLES.ACTIVIST_STAFF || role === ROLES.ACTIVIST_STAFF) {
    return false;
  }
  return ORG_ROLE_RANK[role] >= ORG_ROLE_RANK[required];
}

/** True when `role` satisfies at least one of `allowed`. */
export function hasAnyRole(role: Role | null, allowed: readonly Role[]): boolean {
  return allowed.some((required) => hasRole(role, required));
}

/**
 * The backend's spelling of each value, as a sign-in reply gives it.
 *
 * Two maps, because the backend stopped having one word for both. `kind` says
 * whether this is an Activist Technologies account; `role` says what somebody
 * may do inside one organization, and they may hold a different one in each.
 * Flattening the two back into a single value is exactly the bug this split
 * exists to prevent, so the mapping lives here and nowhere else.
 */
const ROLE_FROM_BACKEND: Record<string, Role> = {
  SUPER_ADMIN: ROLES.SUPER_ADMIN,
  ADMIN: ROLES.ADMIN,
};

/**
 * The role somebody holds in one organization.
 *
 * Returns null for anything unrecognised rather than guessing. A role the
 * portal has never heard of should stop the sign-in, not quietly land
 * somebody on a page they may not be entitled to.
 */
export function organizationRoleFromBackend(
  value: string | null | undefined,
): Role | null {
  if (!value) return null;
  return ROLE_FROM_BACKEND[value] ?? null;
}

/** The backend's name for an Activist Technologies account. */
export const ACTIVIST_STAFF_KIND = 'ACTIVIST_STAFF';

/** True when a sign-in reply describes one of your own employees. */
export function isActivistStaffKind(kind: string | null | undefined): boolean {
  return kind === ACTIVIST_STAFF_KIND;
}
